import deviceConfig from "../../config/usb-devices.json";
import type { Protocol } from "../types";

export interface InterfaceMatch {
  interfaceNumber: number;
  alternateSetting: number;
  endpointIn?: USBEndpoint;
  endpointOut?: USBEndpoint;
  interfaceClass?: number;
  interfaceSubclass?: number;
  interfaceProtocol?: number;
}

function everyInterface(device: USBDevice): InterfaceMatch[] {
  return device.configurations.flatMap((configuration) =>
    configuration.interfaces.flatMap((usbInterface) => {
      const alternates = usbInterface.alternates ?? [usbInterface.alternate];
      return alternates.map((alternate) => ({
        interfaceNumber: usbInterface.interfaceNumber,
        alternateSetting: alternate.alternateSetting,
        endpointIn: alternate.endpoints.find((endpoint) => endpoint.direction === "in" && endpoint.type === "bulk"),
        endpointOut: alternate.endpoints.find((endpoint) => endpoint.direction === "out" && endpoint.type === "bulk"),
        interfaceClass: alternate.interfaceClass,
        interfaceSubclass: alternate.interfaceSubclass,
        interfaceProtocol: alternate.interfaceProtocol,
      }));
    }),
  );
}

export function matchProtocolInterface(device: USBDevice, protocol: Protocol): InterfaceMatch | undefined {
  if (protocol === "AMLOGIC") {
    if (!(deviceConfig.amlogic.vendorIds as number[]).includes(device.vendorId)) return undefined;
    return everyInterface(device).find((item) => item.endpointIn && item.endpointOut);
  }
  const expected = protocol === "ADB" ? deviceConfig.adb.interface : deviceConfig.fastboot.interface;
  return everyInterface(device).find(
    (item) =>
      item.interfaceClass === expected.classCode &&
      item.interfaceSubclass === expected.subclassCode &&
      item.interfaceProtocol === expected.protocolCode &&
      item.endpointIn &&
      item.endpointOut,
  );
}

export function matchesProtocolDevice(device: USBDevice, protocol: Protocol): boolean {
  return Boolean(matchProtocolInterface(device, protocol));
}

/**
 * Detect an Amlogic device for auto-connect. Legacy WorldCup devices can
 * expose incomplete interface descriptors while they are in BL1/TPL, so the
 * known VID/PID filters must be accepted even when bulk endpoints are not
 * available yet. V3 devices still fall back to full interface matching.
 */
export function isAmlogicAutoConnectDevice(device: USBDevice): boolean {
  const knownIdentity = webUsbFilters("AMLOGIC").some((filter) => filter.vendorId === device.vendorId && filter.productId === device.productId);
  return knownIdentity || matchesProtocolDevice(device, "AMLOGIC");
}

export function webUsbFilters(protocol: Protocol): USBDeviceFilter[] {
  if (protocol === "ADB") return deviceConfig.adb.filters;
  if (protocol === "FASTBOOT") return deviceConfig.fastboot.filters;
  return deviceConfig.amlogic.filters;
}

export function legacyAmlogicUsbFilters(): USBDeviceFilter[] {
  return deviceConfig.amlogic.legacyFilters;
}

export function usbVersion(device: USBDevice): string {
  return `${device.usbVersionMajor}.${device.usbVersionMinor}.${device.usbVersionSubminor}`;
}
